import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  signInWithCustomToken,
  sendPasswordResetEmail
} from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import firebaseApp from '../firebase/firebaseConfig.js';

const firebaseAuth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);

// Helper function to create initial user document based on schema
const createInitialUserDoc = (user, authProvider) => {
  const now = Timestamp.now();
  
  return {
    // ===== USER IDENTIFICATION =====
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
    mobileNumber: user.phoneNumber || '',
    skills: [],
    
    // ===== PLAN INFORMATION =====
    planType: 'free',
    planStartDate: null,
    planExpiryDate: null,
    isActivePlan: false,
    
    // ===== PLAN USAGE TRACKING =====
    resumeDownloadsUsed: 0,
    coverLetterDownloadsUsed: 0,
    atsChecksUsed: 0,
    
    // ===== PLAN LIMITS (Free plan defaults) =====
    resumeDownloadLimit: 0,
    coverLetterDownloadLimit: 0,
    atsCheckLimit: 5,
    
    // ===== PAYMENT INFORMATION =====
    lastPaymentDate: null,
    transactionId: '',
    paymentAmount: 0,
    
    // ===== ADDITIONAL FEATURES ACCESS =====
    hasAICoaching: false,
    hasUnlimitedStorage: false,
    hasMockInterview: false,
    hasHRConsultancy: false,
    
    // ===== ACCOUNT METADATA =====
    createdAt: now,
    updatedAt: now,
    isEmailVerified: user.emailVerified || false,
    authProvider: authProvider,
    lastLoginAt: now
  };
};

// Helper function to check if user exists and create if not
const ensureUserDocument = async (user, authProvider) => {
  try {
    // Use 'users' collection for user documents
    const userRef = doc(firestore, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    const isNewUser = !userSnap.exists();
    
    if (isNewUser) {
      // New user - create full document
      const userDoc = createInitialUserDoc(user, authProvider);
      
      await setDoc(userRef, userDoc);
      
      // Verify the document was created
      const verifySnap = await getDoc(userRef);
      if (verifySnap.exists()) {
        // Document verified
      } else {
        console.error('❌ Document creation failed - not found after setDoc');
      }
      
      // Trigger welcome email for new users
      // Import dynamically to avoid circular dependencies
      import('../services/emailService.js').then((module) => {
        const emailService = module.default;
        emailService.sendWelcomeEmail(
          user.uid,
          user.email || '',
          user.displayName || 'User'
        ).then(() => {
          // Welcome email sent
        }).catch((error) => {
          // Don't throw - email failure shouldn't block registration
        });
      }).catch((error) => {
        // Email service load failure is non-critical
      });
    } else {
      // Existing user - update last login + fill in displayName if missing
      const existingData = userSnap.data();
      const updatePayload = {
        lastLoginAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      // If Firestore displayName is empty but user now has one (from Google or updateProfile), fill it in
      if ((!existingData.displayName || existingData.displayName.trim() === '') && user.displayName) {
        updatePayload.displayName = user.displayName;
      }
      // Also update photoURL if Firestore is empty but user now has one (e.g. Google profile pic)
      if (!existingData.photoURL && user.photoURL) {
        updatePayload.photoURL = user.photoURL;
      }
      await setDoc(userRef, updatePayload, { merge: true });
    }
  } catch (error) {
    console.error('❌ Error ensuring user document:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', JSON.stringify(error, null, 2));
    
    // Check if it's a Firestore permission error
    if (error.code === 'permission-denied') {
      console.error('🚨 PERMISSION DENIED: Firestore security rules are blocking write access');
      console.error('Please check your Firestore security rules in Firebase Console');
    }
    
    throw error;
  }
};

export const authService = {
  // Auth state listener
  onAuthStateChange(callback) {
    return onAuthStateChanged(firebaseAuth, callback);
  },

  // Get current user
  getCurrentUser() {
    return firebaseAuth.currentUser;
  },

  // Email registration
  async registerUser(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      const user = userCredential.user;

      // Update profile with display name
      await updateProfile(user, { displayName });

      // Create user document in ResumeGlobeUsers collection
      await ensureUserDocument(user, 'email');

      return { success: true, user: user };
    } catch (error) {
      console.error('❌ Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please sign in instead, or use Google sign-in if you registered with Google.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      }

      return { success: false, error: errorMessage };
    }
  },

  // Email login
  async loginUser(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const user = userCredential.user;

      // Ensure user document exists and update last login
      await ensureUserDocument(user, 'email');

      // Record login timestamp for session timeout tracking
      sessionStorage.setItem('rg_session_login_ts', Date.now().toString());

      return { success: true, user: user };
    } catch (error) {
      console.error('❌ Login error:', error);
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Email address not registered. Kindly signup now!';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password. Please try again.';
      }

      return { success: false, error: errorMessage };
    }
  },

  // Logout
  async logoutUser() {
    try {
      // Clean up reCAPTCHA
      this.cleanupRecaptcha();
      
      // Sign out from Firebase
      await signOut(firebaseAuth);
      
      // Clear any local storage
      localStorage.removeItem('user');
      sessionStorage.clear();
      
      return { success: true, message: 'Signed out successfully' };
    } catch (error) {
      console.error('❌ Logout error:', error);
      return { 
        success: false, 
        error: 'Failed to sign out. Please try again.' 
      };
    }
  },

  // Social login
  async socialLogin(provider) {
    try {
      let authProvider;
      
      switch (provider.toLowerCase()) {
        case 'google':
          authProvider = new GoogleAuthProvider();
          authProvider.addScope('profile');
          authProvider.addScope('email');
          break;
          
        case 'apple':
          authProvider = new OAuthProvider('apple.com');
          authProvider.addScope('email');
          authProvider.addScope('name');
          break;
          
        case 'microsoft':
          authProvider = new OAuthProvider('microsoft.com');
          authProvider.setCustomParameters({
            tenant: 'common'
          });
          authProvider.addScope('User.Read');
          break;
          
        case 'facebook':
          authProvider = new FacebookAuthProvider();
          authProvider.addScope('public_profile');
          authProvider.addScope('email');
          break;
          
        default:
          throw new Error('Unsupported authentication provider');
      }

      const result = await signInWithPopup(firebaseAuth, authProvider);
      const user = result.user;

      // Ensure user document exists in ResumeGlobeUsers collection
      // For existing users, this only updates lastLoginAt + displayName (no plan overrides)
      await ensureUserDocument(user, provider.toLowerCase());

      // Record login timestamp for session timeout tracking
      sessionStorage.setItem('rg_session_login_ts', Date.now().toString());

      return { success: true, user: user };
    } catch (error) {
      console.error(`❌ ${provider} login error:`, error);
      let errorMessage = `${provider.charAt(0).toUpperCase() + provider.slice(1)} login failed. Please try again.`;
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Login was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked. Please allow popups for this site.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Only one popup request is allowed at a time.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email address. Please sign in using the method you originally registered with (e.g., Email/Password or Google), then link additional providers from your profile.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized. Please contact support.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      return { success: false, error: errorMessage };
    }
  },

  // Phone OTP methods
  recaptchaVerifier: null,

  setupRecaptcha() {
    // Reuse existing verifier if still valid (prevents re-triggering reCAPTCHA on resend)
    if (this.recaptchaVerifier) {
      return this.recaptchaVerifier;
    }

    try {
      this.recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          this.cleanupRecaptcha();
        }
      });
    } catch (error) {
      console.error('❌ Error creating reCAPTCHA:', error);
    }
    return this.recaptchaVerifier;
  },

  cleanupRecaptcha() {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      } catch (e) {
        this.recaptchaVerifier = null;
      }
    }
  },

  async sendOTP(phoneNumber) {
    try {
      const appVerifier = this.setupRecaptcha();
      
      const confirmationResult = await signInWithPhoneNumber(firebaseAuth, phoneNumber, appVerifier);
      return { 
        success: true, 
        confirmationResult: confirmationResult 
      };
    } catch (error) {
      console.error('❌ OTP send error:', error);
      this.cleanupRecaptcha();
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      
      switch (error.code) {
        case 'auth/invalid-phone-number':
          errorMessage = 'Invalid phone number format. Please use format: +[country code][number]';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/quota-exceeded':
          errorMessage = 'SMS quota exceeded. Please try again later';
          break;
        case 'auth/invalid-app-credential':
          errorMessage = 'Phone authentication is not enabled. Please contact support or enable Phone sign-in method in Firebase Console';
          break;
        case 'auth/captcha-check-failed':
          errorMessage = 'reCAPTCHA verification failed. Please try again';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  async verifyOTP(confirmationResult, verificationCode) {
    try {
      const userCredential = await confirmationResult.confirm(verificationCode);
      const user = userCredential.user;
      
      // Ensure user document exists in ResumeGlobeUsers collection
      await ensureUserDocument(user, 'phone');

      // Record login timestamp for session timeout tracking
      sessionStorage.setItem('rg_session_login_ts', Date.now().toString());

      this.cleanupRecaptcha();
      return { success: true, user: user };
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      
      let errorMessage = 'Invalid OTP. Please try again.';
      
      switch (error.code) {
        case 'auth/invalid-verification-code':
          errorMessage = 'Invalid verification code';
          break;
        case 'auth/code-expired':
          errorMessage = 'Verification code has expired. Please request a new one';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      return { success: false, error: errorMessage };
    }
  },

  // ========================================
  // MSG91 OTP Methods
  // ========================================

  async sendOTP_MSG91(phoneNumber) {
    try {
      const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, requestId: data.requestId };
      } else {
        console.error('❌ [MSG91] OTP send failed:', data.error);
        return { success: false, error: data.error || 'Failed to send OTP' };
      }
    } catch (error) {
      console.error('❌ [MSG91] OTP send error:', error);
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  },

  async verifyOTP_MSG91(phoneNumber, otp) {
    try {
      const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber, otp })
      });

      const data = await response.json();

      if (data.success && data.customToken) {
        // Sign in to Firebase with the custom token from backend
        const userCredential = await signInWithCustomToken(firebaseAuth, data.customToken);
        const user = userCredential.user;

        // Ensure user document exists in ResumeGlobeUsers collection
        await ensureUserDocument(user, 'phone-msg91');

        // Record login timestamp for session timeout tracking
        sessionStorage.setItem('rg_session_login_ts', Date.now().toString());

        return { success: true, user: user };
      } else {
        console.error('❌ [MSG91] OTP verification failed:', data.error);
        return { success: false, error: data.error || 'Invalid OTP. Please try again.' };
      }
    } catch (error) {
      console.error('❌ [MSG91] OTP verification error:', error);
      return { success: false, error: 'Verification failed. Please try again.' };
    }
  },

  async resendOTP_MSG91(phoneNumber) {
    try {
      const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to resend OTP' };
      }
    } catch (error) {
      console.error('❌ [MSG91] Resend error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  },

  // Reset password — sends via backend's own email service (avoids spam folder)
  async resetPassword(email) {
    try {
      const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'https://resumeglobe-backend.onrender.com';
      const response = await fetch(`${API_BASE_URL}/api/email/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();

      if (data.success) {
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to send password reset email' };
      }
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: 'Failed to send password reset email. Please try again.' };
    }
  }
};